'use client'

import React, { useState, useMemo, useEffect } from 'react';
import { format, parseISO, isSameDay, addMinutes, startOfWeek, addDays, parse, isBefore, isEqual, startOfDay } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import CustomDatePicker from '@/components/ui/CustomDatePicker';
import { Product, AvailableDate } from '@/app/api/types';
import styles from '@/app/styles/custom-datepicker.module.css';

interface DateTimeSelectionProps {
  availableDates: AvailableDate[];
  timezone: string;
  selectedProduct: Product;
  onDateTimeSelected: (date: Date, timeSlot: string) => void;
  variant?: 'default' | 'horizontal';
}

export const DateTimeSelection: React.FC<DateTimeSelectionProps> = ({
                                                                      availableDates,
                                                                      timezone,
                                                                      selectedProduct,
                                                                      onDateTimeSelected,
                                                                      variant = 'default'
                                                                    }) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);

  const availableDatesList = useMemo(() => {
    return availableDates.map(ad => parseISO(ad.date));
  }, [availableDates]);

  // Create minDate as start of today in Berlin timezone
  const minDate = useMemo(() => {
    const now = new Date();
    const berlinDate = toZonedTime(now, timezone);
    return startOfDay(berlinDate);
  }, [timezone]);

  const isDateDisabled = (date: Date) => {
    // Convert the date to Berlin timezone for comparison
    const berlinDate = toZonedTime(date, timezone);
    return !availableDatesList.some(availableDate => isSameDay(availableDate, berlinDate));
  };

  const generateTimeSlots = (availableDate: AvailableDate, duration: number): string[] => {
    if (duration <= 0) {
      console.error('Invalid duration:', duration);
      return [];
    }

    const generatedSlots: string[] = [];
    const now = new Date();
    const berlinNow = toZonedTime(now, timezone);
    const isToday = isSameDay(parseISO(availableDate.date), berlinNow);

    for (const slot of availableDate.slots) {
      try {
        const startDateTime = parseISO(`${availableDate.date}T${slot.start}`);
        const endDateTime = parseISO(`${availableDate.date}T${slot.end}`);

        let currentTime = toZonedTime(startDateTime, timezone);
        const endTime = toZonedTime(endDateTime, timezone);

        if (isBefore(endTime, currentTime)) {
          console.error('End time is before start time for slot:', slot);
          continue;
        }

        while (isBefore(currentTime, endTime)) {
          const potentialEndTime = addMinutes(currentTime, duration);

          // For today, only include future time slots with some buffer
          if (isToday) {
            // Add 15 minutes buffer to current time to avoid immediate bookings
            const bufferedNow = addMinutes(berlinNow, 12);
            if (isBefore(currentTime, bufferedNow)) {
              currentTime = addMinutes(currentTime, duration);
              continue;
            }
          }

          if (isBefore(potentialEndTime, endTime) || isEqual(potentialEndTime, endTime)) {
            const timeSlot = formatInTimeZone(currentTime, timezone, 'HH:mm');
            generatedSlots.push(timeSlot);
          }

          currentTime = addMinutes(currentTime, duration);
        }
      } catch (error) {
        console.error('Error processing slot:', error, 'for slot:', slot);
      }
    }

    return generatedSlots;
  };

  // Auto-select the first available date with time slots on initial render
  useEffect(() => {
    if (availableDates.length > 0 && !selectedDate) {
      // Sort dates to ensure we get the earliest available
      const sortedDates = [...availableDates].sort((a, b) =>
        parseISO(a.date).getTime() - parseISO(b.date).getTime()
      );

      // Find the first date with available time slots
      for (const availableDate of sortedDates) {
        const dateObj = parseISO(availableDate.date);
        const timeSlots = generateTimeSlots(availableDate, selectedProduct.duration);

        if (timeSlots.length > 0) {
          setSelectedDate(dateObj);
          break;
        }
      }
    }
  }, [availableDates, selectedProduct.duration, timezone]);

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    setSelectedTimeSlot(null);
  };

  const handleTimeSlotSelection = (timeSlot: string) => {
    setSelectedTimeSlot(timeSlot);
    if (selectedDate) {
      onDateTimeSelected(selectedDate, timeSlot);
    }
  };

  const renderTimeSlots = () => {
    if (!selectedDate) return null;

    const availableDate = availableDates.find(ad => isSameDay(parseISO(ad.date), selectedDate));
    if (!availableDate) return <div>No available time slots for the selected date.</div>;

    const timeSlots = generateTimeSlots(availableDate, selectedProduct.duration);

    return (
      <div className={`${variant === 'horizontal' ? 'lg:mt-0 lg:w-full' : 'mt-2'}`}>
        <div className="text-[#111928] text-s font-medium leading-[18px] mb-2 pt-6">
          Wähle die Uhrzeit:
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {timeSlots.map((timeSlot, index) => {
            const startTime = parseISO(`${format(selectedDate, 'yyyy-MM-dd')}T${timeSlot}`);
            const endTime = addMinutes(startTime, selectedProduct.duration);
            const formattedStartTime = format(startTime, 'HH:mm');
            const formattedEndTime = format(endTime, 'HH:mm');
            return (
              <button
                data-analytics-id="funnel-product-timeSelector"
                key={index}
                onClick={() => handleTimeSlotSelection(timeSlot)}
                className={`
                  ${variant === 'horizontal' ? 'px-6 py-4 min-h-[60px]' : 'px-4 py-3'}
                  border rounded-lg text-sm
                  transition-all duration-200 ease-in-out
                  ${selectedTimeSlot === timeSlot
                  ? 'bg-primary border-primary text-white shadow-md'
                  : 'border-gray-200 text-[#111928] bg-white hover:bg-gray-50'
                }
                `}
              >
                {formattedStartTime} - {formattedEndTime}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={`container mx-auto ${variant === 'horizontal' ? 'lg:grid lg:grid-cols-5 lg:gap-6' : 'flex' +
      ' flex-col'}`}>
      <div className={`${variant === 'horizontal' ? 'lg:col-span-2 flex justify-center pr-8' : ''}`}>
        <div>
          <div className="text-[#111928] text-s font-medium leading-[18px] mb-4">
            Wähle einen verfügbaren Tag:
          </div>
          <div className={`w-full max-w-[350px] ${variant === 'horizontal' ? 'ml-auto' : ''}`}>
            <div className={styles.datepickerWrapper}>
              <CustomDatePicker
                selectedDate={selectedDate}
                onChange={handleDateChange}
                availableDates={availableDates}
                timezone={timezone}
                minDate={minDate}
                maxDate={addDays(minDate, 28)}
                isDateDisabled={isDateDisabled}
                calendarClassName={styles.customCalendar}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={variant === 'horizontal' ? 'lg:col-span-3' : ''}>
        {selectedDate && renderTimeSlots()}
      </div>
    </div>
  );
};